import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePotentialAssessmentApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import api from '../../services/api';

const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 560px;
  width: 100%;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  max-height: 90vh;
  overflow-y: auto;
`;

const CardTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const CriteriaRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
`;

const CriteriaLabelWrap = styled.div`
  flex: 1;
`;

const CriteriaLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

// Task 34 — PRD YG §3.1: her boyutta 1 (düşük) ve 5 (yüksek) uç tanım metinleri,
// değerlendiriciye yardım ipucu olarak (performance.potential.dim.{token}.low/high).
const CriteriaHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 3px;
  line-height: 1.4;
`;

const CriteriaSelect = styled.div`
  width: 120px;
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
  margin-top: 4px;
`;

// Task 19/34 — 4 boyut (strategic_thinking/influence KALKTI, impact eklendi); BE
// PotentialScoring.ValidKeys ile birebir aynı token seti (talent.potential.invalidCriterion
// ile reddedilmemesi için).
const CRITERIA_KEYS = [
  'learning_agility',
  'leadership',
  'motivation',
  'impact',
];

const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ id: n, name: String(n) }));

// Task 38 cutover — eski /v1/performance-cycles söküldü; seçici artık PRD
// EvaluationPeriod'a (/v1/evaluation-periods) bağlanır (aynı sözleşme: {id, name}).
function buildCycleAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/evaluation-periods', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((c) => ({ id: c.id, name: c.name }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/evaluation-periods/${id}`);
      const c = res.data?.data;
      return c ? { id: c.id, name: c.name } : null;
    },
  };
}

function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      // EmployeeController yalnız firstName/lastName/fullName [like] bind ediyor —
      // `name[like]` no-op'tu (arama filtrelemiyordu). Bkz. feedback-360/config.
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

const cycleAsync = buildCycleAsync();
const employeeAsync = buildEmployeeAsync();

const EMPTY_CRITERIA = () => Object.fromEntries(CRITERIA_KEYS.map((k) => [k, null]));

export default function PotentialAssessmentsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  // Assess modal state
  const [assessOpen, setAssessOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  const [notes, setNotes] = useState('');
  const [criteriaScores, setCriteriaScores] = useState(EMPTY_CRITERIA());
  const [saving, setSaving] = useState(false);

  const {
    fetching, columns: baseColumns, data, pagination, loadFailed,
    upsert, getById, _delete,
  } = usePotentialAssessmentApi(queryParams);

  const openAssessNew = useCallback(() => {
    setEditingId(null);
    setEmployeeId(null);
    setCycleId(null);
    setNotes('');
    setCriteriaScores(EMPTY_CRITERIA());
    setAssessOpen(true);
  }, []);

  const openAssessEdit = useCallback(async (row) => {
    const full = await getById(row.id);
    if (!full) return;
    setEditingId(row.id);
    setEmployeeId(full.employeeId || null);
    setCycleId(full.cycleId || null);
    setNotes(full.notes || '');
    const scores = EMPTY_CRITERIA();
    if (Array.isArray(full.criteria)) {
      full.criteria.forEach((c) => { scores[c.criterionKey] = c.score; });
    }
    setCriteriaScores(scores);
    setAssessOpen(true);
  }, [getById]);

  const handleSave = useCallback(async () => {
    if (!employeeId) return;
    const allFilled = CRITERIA_KEYS.every((k) => criteriaScores[k] != null);
    if (!allFilled) return;
    setSaving(true);
    const payload = {
      employeeId,
      cycleId: cycleId || undefined,
      notes: notes || undefined,
      criteria: CRITERIA_KEYS.map((k) => ({ criterionKey: k, score: criteriaScores[k] })),
    };
    const ok = await upsert(payload);
    if (ok) setAssessOpen(false);
    setSaving(false);
  }, [employeeId, cycleId, notes, criteriaScores, upsert]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.employeeName || row.id });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({
      isOpen: true, ids,
      name: `${ids.length} ${t('performance.potential.title').toLowerCase()}`,
    });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // "Değerlendir" bir mutasyon modalı açar (upsert) — salt-okur kullanıcıya
  // gösterilmez; menü tamamen boş kalırsa kolon da eklenmez (TalentPoolPage
  // deseninin aynısı).
  const actionOptions = useMemo(() => {
    if (!canWrite('TD')) return [];
    return [
      { type: 'edit', label: t('performance.potential.assess'), onClick: openAssessEdit },
      { type: 'divider' },
      { type: 'delete', label: t('performance.common.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, openAssessEdit, handleDelete]);

  const columns = useMemo(() => {
    const cols = [...baseColumns];
    if (actionOptions.length > 0) {
      cols.push({
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      });
    }
    return cols;
  }, [baseColumns, actionOptions, data]);

  const canSave = employeeId && CRITERIA_KEYS.every((k) => criteriaScores[k] != null);

  return (
    <Container>
      <Overlay $open={assessOpen}>
        {assessOpen && (
          <Card>
            <CardTitle>
              {editingId ? t('performance.potential.assess') : t('performance.potential.assess')}
            </CardTitle>

            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.potential.employee')}
                value={employeeId}
                onChange={setEmployeeId}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.potential.cycle')}
                value={cycleId}
                onChange={setCycleId}
                onSearch={cycleAsync.onSearch}
                onGetById={cycleAsync.onGetById}
              />
            </FieldGroup>

            <SectionLabel>{t('performance.potential.score')}</SectionLabel>

            {CRITERIA_KEYS.map((key) => (
              <CriteriaRow key={key}>
                <CriteriaLabelWrap>
                  <CriteriaLabel>{t(`performance.potential.criteria.${key}`)}</CriteriaLabel>
                  <CriteriaHint>1 — {t(`performance.potential.dim.${key}.low`)}</CriteriaHint>
                  <CriteriaHint>5 — {t(`performance.potential.dim.${key}.high`)}</CriteriaHint>
                </CriteriaLabelWrap>
                <CriteriaSelect>
                  <GenericSelect
                    value={criteriaScores[key]}
                    onChange={(v) => setCriteriaScores((prev) => ({ ...prev, [key]: v }))}
                    options={SCORE_OPTIONS}
                    label={t('performance.potential.score')}
                  />
                </CriteriaSelect>
              </CriteriaRow>
            ))}

            <FieldGroup style={{ marginTop: 16 }}>
              <TextInput
                label={t('performance.potential.notes')}
                type="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </FieldGroup>

            <Actions>
              <Button variant="outline" onClick={() => setAssessOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || saving}
                loading={saving}
              >
                {t('common.save')}
              </Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
      {loadFailed && <LoadErrorBanner />}

      {useMemo(() => (
        <DataTable
          title={t('performance.potential.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('TD')}
          onNewButtonClick={openAssessNew}
          onRowsDelete={canWrite('TD') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        openAssessNew, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
